import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ShieldCheck,
  BookOpen,
  Rocket,
  Wrench,
  Radio,
  Users,
  Building2,
  AlertTriangle,
  Mail,
  Users2,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import HussarTopNav from '../components/HussarTopNav';
import useRouteMeta from '../hooks/useRouteMeta';

/**
 * Sposoby współpracy z HussarAi — public, institutional cooperation page.
 *
 * Doctrine (do not change without approval):
 *   • "Nie sprzedajemy automatycznych decyzji. Sprzedajemy kontrolę,
 *     ślad audytowy i spokój operacyjny."
 *   • No Stripe / paywall / checkout — every CTA routes to /pilot-publiczny
 *     or mailto:audyt@hussarai.org.
 *   • No LeadWing bleed — this is the HussarAi institutional surface.
 *   • All copy via i18n: cooperation.* namespace (PL + EN).
 */

const PATH_ICONS = {
  public: BookOpen,
  pilot: Rocket,
  implementation: Wrench,
  monitoring: Radio,
  workshops: Users,
  license: Building2,
};

// Each path is keyed against the cooperation.paths.* namespace.
// `to` = SPA route, `external` = mailto target.
const PATHS = [
  { key: 'public',         to: '/ai-act-polska' },
  { key: 'pilot',          to: '/pilot-publiczny' },
  { key: 'implementation', external: 'mailto:audyt@hussarai.org?subject=HussarAi%20-%20wsparcie%20wdro%C5%BCeniowe' },
  { key: 'monitoring',     external: 'mailto:audyt@hussarai.org?subject=HussarAi%20-%20monitoring%20zmian' },
  { key: 'workshops',      external: 'mailto:audyt@hussarai.org?subject=HussarAi%20-%20warsztat' },
  { key: 'license',        external: 'mailto:audyt@hussarai.org?subject=HussarAi%20-%20licencja%20instytucjonalna' },
];

const SposobyWspolpracy = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'pl').startsWith('en') ? 'en' : 'pl';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pageUrl = `${origin}/sposoby-wspolpracy`;
  const imageUrl = `${origin}/og/sposoby-wspolpracy-og.png`;

  useRouteMeta({
    marker: 'sposoby-wspolpracy',
    title: t('cooperation.metaTitle'),
    description: t('cooperation.metaDescription'),
    canonical: pageUrl,
    locale: lang === 'en' ? 'en_GB' : 'pl_PL',
    ogImage: imageUrl,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogImageAlt: t('cooperation.heroH1'),
    ogUrl: pageUrl,
  });

  const safetyItems = t('cooperation.safety.items', { returnObjects: true }) || [];

  return (
    <div className="hussar-landing" data-testid="sposoby-wspolpracy-page">
      <HussarTopNav active="cooperation" />

      {/* Hero */}
      <section
        className="hussar-section hussar-hero"
        data-testid="cooperation-hero"
        style={{ paddingTop: 80 }}
      >
        <div className="hussar-container">
          <div
            className="hussar-hero-eyebrow"
            data-testid="cooperation-hero-eyebrow"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 999,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(125, 211, 252, 0.06)',
              color: '#7DD3FC',
              fontSize: 11,
              letterSpacing: 1.2,
              fontWeight: 600,
              marginBottom: 18,
            }}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {t('cooperation.heroEyebrow')}
          </div>
          <h1
            className="hussar-hero-title"
            data-testid="cooperation-hero-h1"
            style={{ maxWidth: 880 }}
          >
            {t('cooperation.heroH1')}
          </h1>
          <p
            className="hussar-hero-subtitle"
            data-testid="cooperation-hero-subtitle"
            style={{ maxWidth: 760, marginTop: 16 }}
          >
            {t('cooperation.heroSubtitle')}
          </p>
          <p
            data-testid="cooperation-hero-doctrine"
            style={{
              marginTop: 22,
              fontSize: 13,
              opacity: 0.65,
              fontStyle: 'italic',
            }}
          >
            {t('cooperation.heroDoctrine')}
          </p>
          <div
            className="hussar-inst-trust-badge"
            data-testid="cooperation-pilot-pill"
            role="note"
            aria-label={t('cooperation.pilotPill')}
            style={{ margin: '20px 0 0', alignSelf: 'flex-start' }}
          >
            <Users2 className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{t('cooperation.pilotPill')}</span>
          </div>
        </div>
      </section>

      {/* Six cooperation paths */}
      <section
        className="hussar-section"
        data-testid="cooperation-paths-section"
        style={{ paddingTop: 16 }}
      >
        <div className="hussar-container">
          <h2
            data-testid="cooperation-paths-heading"
            style={{ fontSize: 28, fontWeight: 700, margin: 0, maxWidth: 720 }}
          >
            {t('cooperation.pathsHeading')}
          </h2>
          <p
            style={{ marginTop: 10, opacity: 0.8, maxWidth: 720, fontSize: 14 }}
            data-testid="cooperation-paths-lead"
          >
            {t('cooperation.pathsLead')}
          </p>

          <div
            data-testid="cooperation-paths-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
              marginTop: 28,
            }}
          >
            {PATHS.map(({ key, to, external }) => {
              const Icon = PATH_ICONS[key] || ShieldCheck;
              const Comp = external ? 'a' : Link;
              const compProps = external ? { href: external } : { to };
              // Optional contextual cross-link (e.g. Implementation → Tarcza
              // Autentyczności). Only renders when the i18n entry defines
              // `crosslinkLabel` + `crosslinkTo` — keeps the other cards untouched.
              const crosslinkLabel = t(`cooperation.paths.${key}.crosslinkLabel`, { defaultValue: '' });
              const crosslinkTo = t(`cooperation.paths.${key}.crosslinkTo`, { defaultValue: '' });
              return (
                <Card
                  key={key}
                  data-testid={`cooperation-card-${key}`}
                  style={{
                    padding: 22,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(125, 211, 252, 0.08)',
                      color: '#7DD3FC',
                    }}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <h3
                    style={{ fontSize: 17, fontWeight: 600, margin: 0 }}
                    data-testid={`cooperation-card-${key}-title`}
                  >
                    {t(`cooperation.paths.${key}.title`)}
                  </h3>
                  <p
                    style={{ fontSize: 14, lineHeight: 1.55, opacity: 0.85, margin: 0 }}
                    data-testid={`cooperation-card-${key}-body`}
                  >
                    {t(`cooperation.paths.${key}.body`)}
                  </p>
                  <div
                    data-testid={`cooperation-card-${key}-price`}
                    style={{
                      fontSize: 12,
                      letterSpacing: 0.8,
                      textTransform: 'uppercase',
                      color: '#9ca3af',
                      borderTop: '1px solid rgba(255,255,255,0.06)',
                      paddingTop: 12,
                      marginTop: 4,
                    }}
                  >
                    {t('cooperation.pricePrefix')}: {t(`cooperation.paths.${key}.price`)}
                  </div>
                  <Comp
                    {...compProps}
                    className="hussar-btn-secondary"
                    data-testid={`cooperation-card-${key}-cta`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.12)',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: 13,
                      color: 'var(--hussar-text-primary, #E5E7EB)',
                      alignSelf: 'flex-start',
                      marginTop: 4,
                    }}
                  >
                    {t(`cooperation.paths.${key}.cta`)} <ArrowRight className="w-3.5 h-3.5" />
                  </Comp>
                  {crosslinkLabel && crosslinkTo && (
                    <Link
                      to={crosslinkTo}
                      data-testid={`cooperation-card-${key}-crosslink`}
                      style={{
                        marginTop: 8,
                        fontSize: 11,
                        opacity: 0.7,
                        textDecoration: 'none',
                        color: '#7DD3FC',
                        alignSelf: 'flex-start',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      → {crosslinkLabel}
                    </Link>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Safety section — Czego nie sprzedajemy */}
      <section
        className="hussar-section"
        data-testid="cooperation-safety-section"
        style={{ paddingTop: 24 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="cooperation-safety-card"
            style={{
              padding: 24,
              borderLeft: '3px solid #F59E0B',
              background: 'rgba(245, 158, 11, 0.04)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <AlertTriangle
                className="w-5 h-5"
                style={{ color: '#F59E0B', flexShrink: 0, marginTop: 2 }}
              />
              <div style={{ width: '100%' }}>
                <h2
                  style={{ fontSize: 20, fontWeight: 600, margin: '0 0 12px' }}
                  data-testid="cooperation-safety-heading"
                >
                  {t('cooperation.safety.heading')}
                </h2>
                <ul
                  data-testid="cooperation-safety-list"
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  {safetyItems.map((item, idx) => (
                    <li
                      key={item}
                      data-testid={`cooperation-safety-item-${idx}`}
                      style={{ fontSize: 14, lineHeight: 1.55, opacity: 0.88 }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
                <p
                  data-testid="cooperation-safety-closing"
                  style={{
                    fontSize: 14,
                    lineHeight: 1.6,
                    opacity: 0.95,
                    fontWeight: 600,
                    margin: 0,
                  }}
                >
                  {t('cooperation.safety.closing')}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className="hussar-section"
        data-testid="cooperation-closing-section"
        style={{ paddingTop: 24 }}
      >
        <div className="hussar-container">
          <h2
            style={{ fontSize: 26, fontWeight: 700, margin: 0, maxWidth: 720 }}
            data-testid="cooperation-closing-headline"
          >
            {t('cooperation.closing.headline')}
          </h2>
          <p
            style={{ marginTop: 12, opacity: 0.85, maxWidth: 760, fontSize: 15, lineHeight: 1.6 }}
            data-testid="cooperation-closing-body"
          >
            {t('cooperation.closing.body')}
          </p>
          <div
            data-testid="cooperation-closing-cta-row"
            style={{ marginTop: 22, display: 'flex', gap: 12, flexWrap: 'wrap' }}
          >
            <Link
              to="/pilot-publiczny"
              className="hussar-btn-primary hussar-btn-large"
              data-testid="cooperation-cta-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '14px 24px',
                borderRadius: 8,
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              {t('cooperation.closing.ctaPrimary')} <Rocket className="w-4 h-4" />
            </Link>
            <a
              href="mailto:audyt@hussarai.org?subject=HussarAi%20-%20kontakt"
              className="hussar-btn-secondary"
              data-testid="cooperation-cta-secondary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '14px 24px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.12)',
                textDecoration: 'none',
                fontWeight: 600,
                color: 'var(--hussar-text-primary, #E5E7EB)',
              }}
            >
              {t('cooperation.closing.ctaSecondary')} <Mail className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Closing doctrine tagline */}
      <section
        className="hussar-section"
        data-testid="cooperation-tagline-section"
        style={{ paddingTop: 24, paddingBottom: 80 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="cooperation-tagline-card"
            style={{
              padding: 28,
              background: 'rgba(125, 211, 252, 0.04)',
              borderLeft: '3px solid #7DD3FC',
              textAlign: 'left',
            }}
          >
            <p
              style={{ fontSize: 22, fontWeight: 700, margin: 0, lineHeight: 1.35 }}
              data-testid="cooperation-tagline-headline"
            >
              {t('cooperation.tagline.headline')}
            </p>
            <p
              style={{ marginTop: 10, fontSize: 16, opacity: 0.85, lineHeight: 1.55 }}
              data-testid="cooperation-tagline-subheadline"
            >
              {t('cooperation.tagline.subheadline')}
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default SposobyWspolpracy;
